import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
  KeyboardEvent,
} from 'react'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  DndContext,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { useDocumentState } from '@/state'
import { cn, createSnapModifier } from '@/utils'
import type { FlattenedDocumentPage } from '@/lib/api/client'
import { useDocumentPageQuery } from '../../queries'
import { useSaveDraggableElement } from '../Annotations/queries'
import {
  Position,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Line } from '../Annotations/Line'
import {
  DEFAULT_STROKE_COLOR,
  DISABLED_STROKE_COLOR,
} from '../Annotations/constants'
import {
  isLine,
  isArrow,
  getSubPageIndexFromAbsoluteY,
  getRelativeY,
} from '../Annotations/utils'
import DocumentGrid from '../DocumentGrid'
import { useListTemplateAttributes } from '@/services/hooks/template_attributes'
import { AnnotationsProvider } from './AnnotationsContext'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import { DraggableElements } from '../DraggableElements'
import {
  QUERY_KEYS as TEMPLATE_ATTRIBUTES_QUERY_KEYS,
  useDeleteTemplateAttribute,
} from '@/services/queries/template_attributes'
import queryClient from '@/queryClient'

export const AnnotationsContainer = ({
  documentPage,
  isReadOnly,
  children,
}: {
  documentPage: FlattenedDocumentPage
  isReadOnly: boolean
  children?: ReactNode
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isOutOfBounds, setIsOutOfBounds] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  const [gridHeight, setGridHeight] = useState(0)
  const [gridWidth, setGridWidth] = useState(0)
  const { data: docData } = useDocumentPageQuery()
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })
  const annotationsCanvasRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const setLinePoint = useDocumentState((state) => state.setLinePoint)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const { mutate: saveElement } = useSaveDraggableElement()
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const dataType = useMemo(
    () => newElementData?.dataType,
    [newElementData?.dataType],
  )
  const type = useMemo(
    () =>
      dataType === 'annotation' ? newElementData?.attributes.type : dataType,
    [dataType, newElementData?.attributes.type],
  )
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage?.id as string,
  })
  const { mutateAsync: deleteTemplateAttribute } = useDeleteTemplateAttribute()

  const onRemove = useCallback(
    async (event: KeyboardEvent<HTMLDivElement>) => {
      const isRemovable = selectedElement && selectedElement.isRemovable
      if (!isRemovable) {
        return
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        await deleteTemplateAttribute({
          templateAttributeId: selectedElement?.id as string,
        })
      }
    },
    [deleteTemplateAttribute, selectedElement],
  )

  useEffect(() => {
    if (gridRef.current) {
      const { width, height } = gridRef.current.getBoundingClientRect()
      setGridHeight(height / 50)
      setGridWidth(width / 50)
    }
  }, [gridRef])

  const addAnnotation = useCallback(
    (clickPosition: Position) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const iconPlaceholder = globalThis.document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const { width: placeholderWidth, height: placeholderHeight } =
        iconPlaceholder.getBoundingClientRect()
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      if (canDropElement) {
        saveElement({
          documentPage,
          position: {
            x:
              clickPosition.x / zoomPercentage -
              canvasRect.x / zoomPercentage -
              placeholderWidth / zoomPercentage / 2 -
              1,
            y:
              clickPosition.y / zoomPercentage -
              canvasRect.y / zoomPercentage -
              placeholderHeight / zoomPercentage / 2 -
              1,
          },
        })
      }
    },
    [
      documentPage,
      annotationsCanvasRef,
      zoomPercentage,
      newElementData,
      canDropElement,
      saveElement,
    ],
  )

  const addLine = useCallback(
    (clickPosition: { x: number; y: number }) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      if (!newElementData.attributes.startPoint && canDropElement) {
        setLinePoint({
          type: 'start',
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
      if (newElementData.attributes.startPoint && canDropElement) {
        saveElement({
          documentPage,
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
    },
    [
      documentPage,
      annotationsCanvasRef,
      newElementData,
      canDropElement,
      zoomPercentage,
      setLinePoint,
      saveElement,
    ],
  )

  const onAddAnnotation = useCallback(
    (event: MouseEvent) => {
      if (!newElementData) return
      if (isLine(type)) {
        addLine({ x: event.clientX, y: event.clientY })
      } else {
        addAnnotation({ x: event.clientX, y: event.clientY })
      }
    },
    [newElementData, type, addAnnotation, addLine],
  )

  useEffect(() => {
    const annotationsCanvas = annotationsCanvasRef.current

    if (isAddingElement && annotationsCanvas) {
      annotationsCanvas.addEventListener('click', onAddAnnotation)
      if (type && isLine(type)) {
        annotationsCanvas.style.cursor = 'crosshair'
      } else {
        annotationsCanvas.style.cursor = 'auto'
      }
    } else if (!isAddingElement && annotationsCanvas) {
      annotationsCanvas.removeEventListener('click', onAddAnnotation)
    }

    return () => {
      if (annotationsCanvas) {
        annotationsCanvas.removeEventListener('click', onAddAnnotation)
        annotationsCanvas.style.cursor = 'auto'
      }
    }
  }, [
    isAddingElement,
    annotationsCanvasRef,
    newElementData,
    type,
    onAddAnnotation,
  ])

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 0,
      tolerance: 5,
    },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 0,
      tolerance: 5,
    },
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  const showContainerBorder = useMemo(
    () =>
      (isAddingElement &&
        newElementData?.documentPageId === documentPage?.id) ||
      isDragging,
    [isAddingElement, newElementData, documentPage, isDragging],
  )

  const restrictToDocumentPage: Modifier = ({
    draggingNodeRect,
    transform,
    ...other
  }) => {
    const containerNodeRect =
      annotationsCanvasRef.current?.getBoundingClientRect()

    if (!draggingNodeRect || !containerNodeRect) {
      return transform
    }

    return restrictToParentElement({
      ...other,
      transform,
      draggingNodeRect,
      containerNodeRect,
    })
  }

  const snapToGrid = useMemo(() => {
    if (showGrid) {
      return createSnapModifier({
        gridDimensions: {
          height: gridHeight,
          width: gridWidth,
        },
        gridContainer: gridRef.current,
      })
    }

    return createSnapModifier({
      gridDimensions: {
        height: 1,
        width: 1,
      },
      gridContainer: gridRef.current,
    })
  }, [showGrid, gridHeight, gridWidth])

  const onDragEnd = useCallback(
    async ({ delta: { x, y }, active }) => {
      setShowGrid(false)
      if (x || y) {
        const { type, data_type } = (active.data?.current as any) || {}
        const currentTemplate = templateAttributes?.find(
          (template) => template.id === active.id,
        )

        const zoomedDeltaX = x / zoomPercentage
        const zoomedDeltaY = y / zoomPercentage

        const currentPosition = (currentTemplate?.template_values
          ?.position as any) || { x: 0, y: 0 }
        const currentX = currentPosition?.x ?? 0
        const currentY = currentPosition?.y ?? 0
        const documentSubPages =
          (documentPage?.template_values?.subPages as Array<string>) ?? []
        const currentSubPageId =
          currentTemplate?.template_values?.subPageId ?? null
        const currentSubPageIndex = Math.max(
          documentSubPages.findIndex((id) => id === currentSubPageId),
          0,
        )
        const absoluteY =
          (currentSubPageIndex === 0
            ? currentY
            : LETTER_PAGE_HEIGHT -
              64 +
              (currentSubPageIndex - 1) * LETTER_PAGE_HEIGHT +
              currentY) + zoomedDeltaY

        const newSubPageIndex = getSubPageIndexFromAbsoluteY(absoluteY)
        const newSubPageId =
          documentSubPages[newSubPageIndex] ?? currentSubPageId
        const newRelativeY = getRelativeY(absoluteY)

        const updatedTemplate = {
          data_type: data_type || type,
          payload: {
            subPageId: newSubPageId,
            position: {
              ...(currentTemplate?.template_values?.position || {}),
              x: currentX + zoomedDeltaX,
              y: newRelativeY,
            },
          },
          templateAttributeId: active.id as string,
        }

        queryClient.setQueriesData(
          {
            queryKey: [
              TEMPLATE_ATTRIBUTES_QUERY_KEYS.TEMPLATE_ATTRIBUTES,
              {
                documentPageId: documentPage?.id,
              },
            ],
          },
          (oldData?: Array<TemplateAttribute<any>>) => {
            const templates = oldData || []

            return templates.map((template) => {
              if (template.id === active.id) {
                return {
                  ...template,
                  template_values: {
                    ...template.template_values,
                    ...updatedTemplate.payload,
                  },
                }
              }

              return template
            })
          },
        )
        void updateTemplate(updatedTemplate)
      }
      setIsDragging(false)
    },
    [
      templateAttributes,
      updateTemplate,
      zoomPercentage,
      documentPage?.template_values?.subPages,
      documentPage?.id,
    ],
  )

  if (
    !docData ||
    !docData.project ||
    !docData.version ||
    !docData.documentPages
  ) {
    return null
  }

  return (
    <AnnotationsProvider
      isOutOfBounds={isOutOfBounds}
      setIsOutOfBounds={setIsOutOfBounds}
      setIsResizing={setIsDragging}
      annotationsCanvasRef={annotationsCanvasRef}
    >
      <DndContext
        sensors={sensors}
        modifiers={[snapToGrid, restrictToDocumentPage]}
        onDragStart={({ active }) => {
          const draggableId = active.id as string
          setSelectedElement(null)

          setSelectedElement({
            documentPageId: documentPage.id as string,
            id: draggableId,
            isRemovable: true,
          })
        }}
        onDragMove={({ active }) => {
          const { type } = (active.data?.current as any) || {}
          setIsDragging(true)

          if (type !== 'annotation') {
            setShowGrid(true)
          }
        }}
        onDragEnd={onDragEnd}
        onDragCancel={() => setIsDragging(false)}
      >
        <div
          className={cn(
            'flex flex-col rounded border flex-1 mb-9 relative justify-between overflow-hidden',
            {
              'border-transparent': !showContainerBorder,
              'border-solid': showContainerBorder,
              'border-primary-40': showContainerBorder,
              'border-red-500': isOutOfBounds,
              'shadow-md': showContainerBorder,
              'shadow-gray-400': showContainerBorder,
            },
          )}
          ref={annotationsCanvasRef}
          id={`droppable-container--${documentPage.id}`}
          onKeyDown={(e) => void onRemove(e)}
        >
          <DocumentGrid
            gridDimensions={{ width: gridWidth, height: gridHeight }}
            showGrid={showGrid}
            ref={gridRef}
          />
          {isAddingElement &&
            newElementData?.documentPageId === documentPage.id &&
            isLine(type || '') &&
            newElementData?.attributes.startPoint && (
              <div className="z-[2] absolute">
                <Line
                  startPoint={newElementData?.attributes.startPoint}
                  endPoint={
                    newElementData?.attributes.endPoint ||
                    newElementData?.attributes.startPoint
                  }
                  strokeOpacity={canDropElement ? 0.6 : 1}
                  strokeColor={
                    canDropElement
                      ? newElementData?.attributes.strokeColor ||
                        DEFAULT_STROKE_COLOR
                      : DISABLED_STROKE_COLOR
                  }
                  isArrow={isArrow(type || '')}
                  isBubble={(type && type?.includes('bubble')) || false}
                  bubbleValue={newElementData?.attributes.bubbleValue}
                  renderWrapper={true}
                  textPosition={type?.split('--')[1] || ''}
                />
              </div>
            )}
          <DraggableElements
            isReadOnly={isReadOnly}
            documentPage={documentPage}
          />
          {children}
        </div>
      </DndContext>
    </AnnotationsProvider>
  )
}
